'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { selectCurrentEscritorio } from '@/features/escritorio';
import { registrarAcao } from '@/shared/utils/reportAction';
import type { AppDispatch } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { criarChaveDeApi, listarChavesDeApi, revogarChaveDeApi, rotacionarChaveDeApi } from '../store/actions';
import { selectChavesAcaoEmCurso, selectChavesCarregadoPara, selectChavesCarregando, selectChavesDeApi, selectChavesErro, selectChavesErroDaAcao, selectTemChaveVigente } from '../store/selectors';
import { limparErroDaAcao } from '../store/slice';
import type { AcaoDasChaves, DadosDaChaveNova, DadosDaRotacao, ErroDasChaves } from '../types/chavesDeApiTypes';

/**
 * Recusas em que a lista em memória ficou velha: outra aba criou (409), outra pessoa revogou (404)
 * ou a chave saiu de vigente antes da rotação (422). Reler mostra o motivo na própria tabela.
 */
const STATUS_QUE_PEDEM_RELEITURA = new Set([404, 409, 422]);

/**
 * Telemetria da tela. Só identificadores e o código do erro: nem o texto da chave, nem o prefixo,
 * nem o nome que o cliente deu — o escritório já vem do contexto global do RUM.
 */
function registrarFalha(acao: AcaoDasChaves | 'listar', erro: ErroDasChaves): void {
  registrarAcao('chavesDeApi.falhou', {
    acao,
    status: erro.status,
    codigo: erro.codigo
  });
}
async function listar(dispatch: AppDispatch, escritorioId: string): Promise<void> {
  const resultado = await dispatch(listarChavesDeApi(escritorioId));
  if (listarChavesDeApi.rejected.match(resultado) && resultado.payload) {
    registrarFalha('listar', resultado.payload);
  }
}
export function useChavesDeApi() {
  const $ = _c(45);
  const dispatch = useAppDispatch();
  const escritorioId = useAppSelector(selectCurrentEscritorio)?.id ?? null;
  const chaves = useAppSelector(selectChavesDeApi);
  const carregadoPara = useAppSelector(selectChavesCarregadoPara);
  const carregando = useAppSelector(selectChavesCarregando);
  const erro = useAppSelector(selectChavesErro);
  const acaoEmCurso = useAppSelector(selectChavesAcaoEmCurso);
  const erroDaAcao = useAppSelector(selectChavesErroDaAcao);
  const temChaveVigente = useAppSelector(selectTemChaveVigente);
  const escritorioAtual = useRef(escritorioId);
  let t0;
  let t1;
  if ($[0] !== escritorioId) {
    t0 = () => {
      escritorioAtual.current = escritorioId;
    };
    t1 = [escritorioId];
    $[0] = escritorioId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== dispatch) {
    t2 = async () => {
      const atual = escritorioAtual.current;
      if (atual) {
        await listar(dispatch, atual);
      }
    };
    $[3] = dispatch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const recarregar = t2;
  let t3;
  let t4;
  if ($[5] !== dispatch || $[6] !== escritorioId) {
    t3 = () => {
      if (escritorioId) {
        listar(dispatch, escritorioId);
      }
    };
    t4 = [dispatch, escritorioId];
    $[5] = dispatch;
    $[6] = escritorioId;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== recarregar) {
    t5 = async (acao, falha) => {
      if (!falha) {
        return;
      }
      registrarFalha(acao, falha);
      if (STATUS_QUE_PEDEM_RELEITURA.has(falha.status)) {
        await recarregar();
      }
    };
    $[9] = recarregar;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const aposFalha = t5;
  let t6;
  if ($[11] !== aposFalha || $[12] !== dispatch || $[13] !== escritorioId || $[14] !== recarregar) {
    t6 = async dados => {
      if (!escritorioId) {
        return null;
      }
      const {
        emitida,
        erro: falha_0
      } = await dispatch(criarChaveDeApi({
        escritorioId,
        dados
      }));
      if (emitida) {
        registrarAcao("chavesDeApi.criada", {
          chaveId: emitida.chave.id
        });
        await recarregar();
        return emitida.texto;
      }
      await aposFalha("criar", falha_0);
      return null;
    };
    $[11] = aposFalha;
    $[12] = dispatch;
    $[13] = escritorioId;
    $[14] = recarregar;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const criar = t6;
  let t7;
  if ($[16] !== aposFalha || $[17] !== dispatch || $[18] !== escritorioId || $[19] !== recarregar) {
    t7 = async (chaveId, dados_0) => {
      if (!escritorioId) {
        return null;
      }
      const {
        emitida: emitida_0,
        erro: falha_1
      } = await dispatch(rotacionarChaveDeApi({
        escritorioId,
        rotacao: {
          chaveId,
          ...dados_0
        }
      }));
      if (emitida_0) {
        registrarAcao("chavesDeApi.rotacionada", {
          chaveId: emitida_0.chave.id,
          chaveAnteriorId: chaveId
        });
        await recarregar();
        return emitida_0.texto;
      }
      await aposFalha("rotacionar", falha_1);
      return null;
    };
    $[16] = aposFalha;
    $[17] = dispatch;
    $[18] = escritorioId;
    $[19] = recarregar;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const rotacionar = t7;
  let t8;
  if ($[21] !== aposFalha || $[22] !== dispatch || $[23] !== escritorioId || $[24] !== recarregar) {
    t8 = async chaveId_0 => {
      if (!escritorioId) {
        return false;
      }
      const resultado = await dispatch(revogarChaveDeApi({
        escritorioId,
        chaveId: chaveId_0
      }));
      if (revogarChaveDeApi.fulfilled.match(resultado)) {
        registrarAcao("chavesDeApi.revogada", {
          chaveId: chaveId_0
        });
        await recarregar();
        return true;
      }
      await aposFalha("revogar", resultado.payload);
      return false;
    };
    $[21] = aposFalha;
    $[22] = dispatch;
    $[23] = escritorioId;
    $[24] = recarregar;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const revogar = t8;
  const carregado = escritorioId !== null && carregadoPara === escritorioId;
  let t9;
  if ($[26] !== carregado || $[27] !== chaves) {
    t9 = carregado ? chaves : [];
    $[26] = carregado;
    $[27] = chaves;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = carregado && temChaveVigente;
  let t11;
  if ($[29] !== dispatch) {
    t11 = () => dispatch(limparErroDaAcao());
    $[29] = dispatch;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== acaoEmCurso || $[32] !== carregado || $[33] !== carregando || $[34] !== criar || $[35] !== erro || $[36] !== erroDaAcao || $[37] !== escritorioId || $[38] !== recarregar || $[39] !== revogar || $[40] !== rotacionar || $[41] !== t10 || $[42] !== t11 || $[43] !== t9) {
    t12 = {
      escritorioId,
      carregado,
      chaves: t9,
      carregando,
      erro,
      acaoEmCurso,
      erroDaAcao,
      temChaveVigente: t10,
      recarregar,
      criar,
      rotacionar,
      revogar,
      limparErroDaAcao: t11
    };
    $[31] = acaoEmCurso;
    $[32] = carregado;
    $[33] = carregando;
    $[34] = criar;
    $[35] = erro;
    $[36] = erroDaAcao;
    $[37] = escritorioId;
    $[38] = recarregar;
    $[39] = revogar;
    $[40] = rotacionar;
    $[41] = t10;
    $[42] = t11;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
}